/**
 * Zoom Component View SDK singleton.
 *
 * ZoomMtgEmbedded.createClient() always returns the SAME module-level object.
 * Every bug we hit (error 3000, blank panel, double-init) came from treating
 * the SDK as a per-component instance. This module is the single owner of that
 * singleton and exposes a safe, sequential API:
 *
 *   joinMeeting(params)  — leaves any current session, inits, then joins
 *   leaveMeeting()       — leaves and waits for Closed state before resolving
 *   onConnectionChange(handler)  — subscribe to SDK connection-change events
 *   offConnectionChange(handler) — unsubscribe
 *   getSdkState()        — current state string for diagnostics
 *
 * All SDK calls go through this module. No component should call
 * ZoomMtgEmbedded directly.
 *
 * Gap 5 scenarios handled:
 *   - Error 3000 (already in meeting): leaveMeeting() is always awaited before join
 *   - Blank panel on remount: init() is always called with the current DOM node
 *   - Double-init: _state guard prevents concurrent init+join calls
 *   - DOM node changes: init() is called on every join with the provided zoomAppRoot
 *   - Network drop: SDK auto-reconnects; we surface connection-change events
 *   - React StrictMode double-invoke: second call hits the _leavePromise await
 */

import ZoomMtgEmbedded from '@zoom/meetingsdk/embedded';

// ── Global async error capture ────────────────────────────────────────────────
// Zoom SDK sometimes throws errors outside our try-catch (e.g. from workers or
// unhandled promise rejections). This catches them so we can see them in the log.
window.addEventListener('unhandledrejection', (evt) => {
  console.error('[ZoomClient][GlobalCapture] Unhandled promise rejection:', evt.reason, evt.reason?.stack);
});
window.addEventListener('error', (evt) => {
  console.error('[ZoomClient][GlobalCapture] Uncaught error:', evt.message, 'at', evt.filename, evt.lineno, evt.colno, evt.error?.stack);
});
// ─────────────────────────────────────────────────────────────────────────────

// 'idle' | 'initializing' | 'joined' | 'leaving'
let _state = 'idle';
let _client = null;
let _leavePromise = null;
// Guards the one-time connection-change listener registration.
// The SDK client object is NOT ready to accept .on() calls immediately after
// createClient() — it must be called after init() resolves.
let _listenerRegistered = false;

// All registered external connection-change listeners
const _handlers = new Set();

function _setState(next) {
  console.log(`[ZoomClient] state: ${_state} → ${next}`);
  _state = next;
}

function _getClient() {
  if (!_client) {
    console.log('[ZoomClient] Creating SDK client singleton');
    _client = ZoomMtgEmbedded.createClient();
    // ⚠️  Do NOT call _client.on() here.
    // The SDK's internal event registry is undefined until init() has run.
    // Calling .on() here causes "Cannot read properties of undefined (reading 'includes')".
    // See _registerConnectionListener() which is called after init() succeeds.
  }
  return _client;
}

/**
 * Register the permanent connection-change listener on the SDK client.
 * Must only be called AFTER client.init() has resolved.
 * Safe to call multiple times — registers at most once.
 */
function _registerConnectionListener() {
  if (_listenerRegistered) return;
  console.log('[ZoomClient] Registering connection-change listener (post-init)');
  _getClient().on('connection-change', (evt) => {
    console.log('[ZoomClient] connection-change:', JSON.stringify(evt));
    _handlers.forEach((h) => h(evt));
    if (evt.state === 'Closed' || evt.state === 'Fail') {
      _setState('idle');
    }
  });
  _listenerRegistered = true;
}

/**
 * Leave the current meeting and wait until the SDK reaches Closed/Fail state
 * (or a 5-second safety timeout). Safe to call when not in a meeting.
 */
export async function leaveMeeting() {
  console.log(`[ZoomClient] leaveMeeting() called — current state: ${_state}`, new Error('call stack').stack.split('\n').slice(1, 4).join(' | '));
  // Already leaving — return the same promise so concurrent callers all wait
  if (_leavePromise) {
    console.log('[ZoomClient] leaveMeeting() — already leaving, awaiting existing promise');
    return _leavePromise;
  }
  if (_state === 'idle') {
    console.log('[ZoomClient] leaveMeeting() — state is idle, nothing to do');
    return;
  }

  // SDK never completed a join — calling leaveMeeting() on an unjoined client
  // sends a leave command that can end the Zoom meeting server-side (bad).
  // Just reset state without touching the SDK.
  if (_state === 'initializing') {
    console.log('[ZoomClient] leaveMeeting() — state is initializing, resetting to idle without SDK call');
    _setState('idle');
    return;
  }

  _setState('leaving');

  _leavePromise = new Promise((resolve) => {
    const cleanup = () => {
      _state = 'idle';
      _leavePromise = null;
      resolve();
    };

    const timeout = setTimeout(cleanup, 5000);

    const onClosed = (evt) => {
      if (evt.state === 'Closed' || evt.state === 'Fail') {
        clearTimeout(timeout);
        _handlers.delete(onClosed);
        cleanup();
      }
    };
    _handlers.add(onClosed);

    try {
      _getClient().leaveMeeting();
    } catch {
      clearTimeout(timeout);
      _handlers.delete(onClosed);
      cleanup();
    }
  });

  return _leavePromise;
}

/**
 * Join a meeting.
 *
 * Sequence:
 *   1. Await any pending leaveMeeting() call
 *   2. Force-leave if somehow still joined
 *   3. init() with the provided DOM node and view dimensions
 *   4. join() with the provided credentials
 *
 * @param {object} params
 * @param {HTMLElement} params.zoomAppRoot  — The container div (must be in DOM)
 * @param {number}      params.viewWidth    — Container width in px
 * @param {number}      params.viewHeight   — Container height minus ~48px toolbar
 * @param {string}      params.sdkKey
 * @param {string}      params.signature
 * @param {string}      params.meetingNumber
 * @param {string}      params.password
 * @param {string}      params.userName
 * @param {string}      params.userEmail
 * @param {string}      [params.zak]        — Host ZAK token (teacher only)
 * @param {string}      [params.webEndpoint] — Regional cluster, e.g. us05web.zoom.us
 */
export async function joinMeeting({
  zoomAppRoot,
  viewWidth,
  viewHeight,
  sdkKey,
  signature,
  meetingNumber,
  password,
  userName,
  userEmail,
  zak,
  webEndpoint,
}) {
  console.log('[ZoomClient] joinMeeting() called — state:', _state, {
    meetingNumber,
    userName,
    userEmail,
    hasSignature: !!signature,
    hasZak: !!zak,
    webEndpoint,
    viewWidth,
    viewHeight,
    zoomAppRootInDOM: zoomAppRoot ? document.body.contains(zoomAppRoot) : false,
  });

  // Wait for any in-flight leave before starting
  if (_leavePromise) {
    console.log('[ZoomClient] joinMeeting() — waiting for in-flight leave to finish');
    await _leavePromise;
  }
  if (_state === 'joined') {
    console.log('[ZoomClient] joinMeeting() — already joined, leaving first');
    await leaveMeeting();
  }

  const client = _getClient();
  _setState('initializing');

  const initConfig = {
    zoomAppRoot,
    language: 'en-US',
    patchJsMedia: true,
    assetPath: '/lib',
    customize: {
      video: {
        viewSizes: { default: { width: viewWidth, height: viewHeight } },
        defaultViewType: 'gallery',
        isResizable: false,
      },
    },
  };
  // webEndpoint must be set on init(), not join() — the SDK reads it from the
  // module-level variable set during init and ignores it in joinOptions.
  if (webEndpoint) initConfig.webEndpoint = webEndpoint;

  console.log('[ZoomClient] calling client.init() with config:', { ...initConfig, zoomAppRoot: '[HTMLElement]' });
  try {
    await client.init(initConfig);
    console.log('[ZoomClient] client.init() resolved OK');
    // Register the connection-change listener now that the SDK is ready.
    _registerConnectionListener();
  } catch (initErr) {
    console.error('[ZoomClient] client.init() THREW:', initErr);
    _setState('idle');
    throw initErr;
  }

  // sdkKey in joinOptions is deprecated since SDK v4; pass only what's needed.
  const joinConfig = { signature, meetingNumber, password, userName, userEmail };
  if (zak) joinConfig.zak = zak;

  console.log('[ZoomClient] calling client.join() with config:', { ...joinConfig, signature: '[jwt]', zak: zak ? '[zak]' : undefined });
  try {
    await client.join(joinConfig);
    console.log('[ZoomClient] client.join() resolved OK');
  } catch (joinErr) {
    console.error('[ZoomClient] client.join() THREW:', joinErr);
    _setState('idle');
    throw joinErr;
  }

  _setState('joined');
}

/** Subscribe to SDK connection-change events. Handler receives { state, reason? }. */
export function onConnectionChange(handler) {
  _handlers.add(handler);
}

/** Unsubscribe a connection-change handler. */
export function offConnectionChange(handler) {
  _handlers.delete(handler);
}

/** Current SDK state — for diagnostics. */
export function getSdkState() {
  return _state;
}
